import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import MaturaPiecewiseInequalityExplanationGraph from "../../temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/MaturaPiecewiseInequalityExplanationGraph";
import {
  getIntervalRaw,
  intervalsEqualList,
  parseIntervalsUnion,
  parseSingleInterval,
  randInt,
} from "../../temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/helpers/MaturaPiecewiseFunctionUtils";

const RELATIONS = [
  { symbol: ">", operator: ">", includeBoundary: false, outside: true },
  { symbol: "\\ge", operator: ">=", includeBoundary: true, outside: true },
  { symbol: "<", operator: "<", includeBoundary: false, outside: false },
  { symbol: "\\le", operator: "<=", includeBoundary: true, outside: false },
];

function interval(left, right, leftClosed, rightClosed) {
  return { left, right, leftClosed, rightClosed };
}

function intervalLatex(value) {
  return `${value.leftClosed ? "[" : "("}${value.left},\\,${value.right}${
    value.rightClosed ? "]" : ")"
  }`;
}

function intervalsLatex(values) {
  return values.map(intervalLatex).join("\\cup");
}

function buildData() {
  const h = randInt(-1, 1);
  const v = randInt(-2, 0);
  const leftDistance = randInt(3, 4);
  const keepSymmetric = Math.random() < 0.15;
  const rightDistance = keepSymmetric
    ? leftDistance
    : leftDistance === 3 ? 4 : 3;
  const xL = h - leftDistance;
  const xR = h + rightDistance;
  const referenceSide = Math.random() < 0.5 ? -1 : 1;
  const referenceX = h + referenceSide;
  const referenceValue = v + 1;
  const leftBoundary = h - 1;
  const rightBoundary = h + 1;
  const relation = RELATIONS[randInt(0, RELATIONS.length - 1)];

  const answerIntervals = relation.outside
    ? [
        interval(xL, leftBoundary, true, relation.includeBoundary),
        interval(rightBoundary, xR, relation.includeBoundary, true),
      ]
    : [
        interval(
          leftBoundary,
          rightBoundary,
          relation.includeBoundary,
          relation.includeBoundary
        ),
      ];

  return {
    relation,
    referenceX,
    referenceValue,
    answerIntervals,
    points: {
      A: { x: xL, y: v + leftDistance, markerSymbol: "circle-filled", showLabel: false },
      B: { x: h, y: v, showDot: false, showMarker: false, showLabel: false },
      C: { x: xR, y: v + rightDistance, markerSymbol: "circle-filled", showLabel: false },
    },
    segments: [{ p1: "A", p2: "B" }, { p1: "B", p2: "C" }],
    xRange: [-6, 6],
    yRange: [-6, 6],
  };
}

export function generateTask() {
  return { type: "ReadFunctionComparisonIntervalFromGraphTask", data: buildData() };
}

export function renderTask({ data }) {
  const relationLatex = `f(x)\\,${data.relation.symbol}\\,f(${data.referenceX})`;
  return (
    <div className="flex flex-col gap-3">
      <p>
        Na podstawie wykresu wyznacz zbiór wszystkich argumentów spełniających
        nierówność <InlineMath math={relationLatex} />.
      </p>
      <div style={{ margin: "0 auto", maxWidth: 330 }}>
        <GraphViewer
          width={330}
          points={data.points}
          segments={data.segments}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>
      <p>Zapisz odpowiedź w postaci przedziału lub sumy przedziałów.</p>
    </div>
  );
}

export function renderInput(_task, userInput = {}, onChange, disabled) {
  const raw = getIntervalRaw(userInput, "I");
  return (
    <div className="flex flex-col gap-2">
      <div><InlineMath math="x\\in" /></div>
      <IntervalInput
        value={userInput.I ?? ""}
        onChange={(value) => onChange({ ...(userInput || {}), I: value })}
        allowUnion
        disabled={disabled}
        showPreview={false}
      />
      {raw ? <InlineMath math={`x\\in ${raw}`} /> : null}
    </div>
  );
}

export function checkAnswer({ data }, userInput = {}) {
  const raw = getIntervalRaw(userInput, "I");
  const parsed = data.answerIntervals.length === 1
    ? parseSingleInterval(raw)
    : parseIntervalsUnion(raw);
  if (!parsed) return false;
  return intervalsEqualList(
    data.answerIntervals,
    Array.isArray(parsed) ? parsed : [parsed]
  );
}

export function renderAnswer({ data }) {
  return <InlineMath math={`x\\in ${intervalsLatex(data.answerIntervals)}`} />;
}

export function renderExplanation({ data }) {
  const relationLatex = `f(x)\\,${data.relation.symbol}\\,f(${data.referenceX})`;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p><strong>Krok 1.</strong> Najpierw odczytujemy z wykresu wartość wskazaną po prawej stronie:</p>
      <BlockMath math={`f(${data.referenceX})=${data.referenceValue}`} />
      <p>
        <strong>Krok 2.</strong> Nierówność <InlineMath math={relationLatex} /> oznacza więc
        porównanie wartości funkcji z poziomem <InlineMath math={`y=${data.referenceValue}`} />.
      </p>
      <p>
        <strong>Krok 3.</strong> Wybieramy fragmenty wykresu leżące
        {data.relation.outside ? " powyżej" : " poniżej"} tego poziomu i odczytujemy
        odpowiadające im argumenty z osi <InlineMath math="OX" />.
      </p>
      <div style={{ width: "min(100%, 620px)", margin: "12px auto" }}>
        <MaturaPiecewiseInequalityExplanationGraph
          points={data.points}
          segments={data.segments}
          solutionIntervals={data.answerIntervals}
          threshold={data.referenceValue}
          operator={data.relation.operator}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>
      <BlockMath math={`x\\in ${intervalsLatex(data.answerIntervals)}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = getIntervalRaw(userAnswer, "I");
  return raw ? <InlineMath math={`x\\in ${raw}`} /> : <i>(brak odpowiedzi)</i>;
}

const ReadFunctionComparisonIntervalFromGraphTask = {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};

export default ReadFunctionComparisonIntervalFromGraphTask;
